import { useTranslation } from 'next-i18next'
import React from 'react'
import { Text, View } from 'react-native'
import { useMediaQuery } from 'react-responsive'

import { stepContent } from '@/components/flowStep/styles'
import { useAnalytics } from '@/context/AnalyticsContext'
import { useFormatting } from '@/context/FormattingContext'
import { apiBases, getApiService } from '@/services/api'
import { AirpodsNotEligibleModal, AirpodsTopBar } from '@/shared/components/flows/AirpodsCampaign'
import { SomethingWentWrongFlow } from '@/shared/components/flows/SomethingWentWrong'
import { StepValidationAlert } from '@/shared/components/flows/StepValidationAlert'
import { ZodForm } from '@/shared/components/flows/ZodForm'
import BaseFlowLayout from '@/shared/components/flows/layout'
import { isAirpodsCoupon } from '@/utils/airpodsCampaign'
import { getLocaleParts } from '@/utils/locale'
import { FlowProgress } from '@flows-ui/components/Progress'
import type { PriceChangeUserAction } from '@flows-ui/services/QuoteDataContext.types'
import { StepContentRenderer } from '@flows-ui/stepRenderer'
import type { QuoteDataContextValue } from '@flows-ui/stepRenderer'
import { processStepContentElementText } from '@flows-ui/utils/processStepContentElementText'
import { extractTrackingFlowValues, getPremiumIntervalFromValues } from '@flows-ui/utils/tracking'
import { useUserPriceChangedEvent } from '@flows-ui/utils/useUserPriceChangedEvent'
import { EVENTS } from '@flows/configs/analytics/constants'
import { shouldAutoSubmit } from '@flows/renderer/utils/forms'
import Spinner from '@ui-kit/components/Spinner'
import { FieldSizeProvider } from '@ui-kit/components/formComponents/FieldSizeContext'
import { SCREEN_SIZE } from '@ui-kit/constants'

import { FlowOrderSummary } from './FlowOrderSummary'
import type { FlowOrderSummaryProps } from './FlowOrderSummary'
import { FlowStepBackLink } from './FlowStepBackLink'
import { FlowStepFooter } from './FlowStepFooter'
import { OtpModal } from './OtpModal'
import type { FlowStepPageConfig, FlowStepPageProps } from './types'
import { useFlowComponentMap } from './useFlowComponentMap'
import { useFlowDiscounts } from './useFlowDiscounts'
import { useFlowFeatureFlags } from './useFlowFeatureFlags'
import { readFlowSessionId, useFlowQueryParams, useFlowStepSession } from './useFlowStepSession'
import { useFlowSubmit } from './useFlowSubmit'
import { useFlowSummary } from './useFlowSummary'

const isProgressiveStep = (config: FlowStepPageConfig, stepName: string) =>
  Boolean(config.progressiveSteps?.includes(stepName))

const isWideOrderSummary = (config: FlowStepPageConfig, showOrderSummary: boolean) =>
  Boolean(config.wideOrderSummary) && showOrderSummary

const getOrderSummaryComponent = (config: FlowStepPageConfig) =>
  config.orderSummaryComponent ?? FlowOrderSummary

const DesktopOrderSummary = ({
  show,
  wide,
  children,
}: {
  show: boolean
  wide: boolean
  children: React.ReactNode
}) => {
  const { orderSummaryCardDesktop, orderSummaryColumn } = stepContent({ wideOrderSummary: wide })
  if (!show) {
    return null
  }
  if (!wide) {
    return <View className={orderSummaryCardDesktop()}>{children}</View>
  }
  return (
    <View className={orderSummaryColumn()}>
      <View className={orderSummaryCardDesktop()}>{children}</View>
    </View>
  )
}

const getPremiumInterval = (flowValues: Record<string, unknown>): 'P1Y' | 'P1M' =>
  flowValues.premium_interval === 'P1Y' ? 'P1Y' : 'P1M'

const getStepValidationKey = (
  error: { message: string; data?: { stepValidation?: boolean } | null } | null
) => (error?.data?.stepValidation === true ? error.message : null)

const getStepLayout = (
  config: FlowStepPageConfig,
  data: { stepFlags: { isQuoteStep: boolean; isSuccessStep: boolean }; showOrderSummary: boolean },
  { navigating, isPlaceholderData }: { navigating: boolean; isPlaceholderData: boolean }
) => {
  const { isQuoteStep, isSuccessStep } = data.stepFlags
  const showOrderSummary = data.showOrderSummary && !isSuccessStep
  return {
    isTransitioning: navigating || isPlaceholderData,
    isQuoteStep,
    isSuccessStep,
    showOrderSummary,
    wideOrderSummary: isWideOrderSummary(config, showOrderSummary),
    wideContent: data.showOrderSummary || isSuccessStep,
  }
}

const getContentClassName = (
  config: FlowStepPageConfig,
  layout: ReturnType<typeof getStepLayout>
) => {
  const styles = stepContent()
  if (layout.isSuccessStep) {
    return styles.successContentWrapper()
  }
  if (layout.isQuoteStep && config.wideQuoteStep) {
    return `${styles.wideStepContentContainer()} mx-auto`
  }
  return `${styles.contentContainer({ wideOrderSummary: layout.wideOrderSummary })} mx-auto`
}

const canAutoSubmitStep = ({
  backBtnPressed,
  couponPending,
  isFormSyncedForStep,
  isPriceRequestPending,
  isTransitioning,
  stepContent: elements,
}: {
  backBtnPressed: boolean
  couponPending: boolean
  isFormSyncedForStep: boolean
  isPriceRequestPending: boolean
  isTransitioning: boolean
  stepContent: Parameters<typeof shouldAutoSubmit>[0]
}) =>
  !backBtnPressed &&
  !isTransitioning &&
  !couponPending &&
  !isPriceRequestPending &&
  isFormSyncedForStep &&
  shouldAutoSubmit(elements)

const applyLiveConditions = <TElement extends object, TCondition>(
  elements: readonly TElement[],
  conditions: Record<number, TCondition | undefined>
) =>
  elements.map((element, index) => {
    const liveCondition = conditions[index]
    return liveCondition ? { ...element, condition: liveCondition } : element
  })

const getContentWrapperClassName = (layout: ReturnType<typeof getStepLayout>) =>
  layout.wideContent ? stepContent().wideContentWrapper() : 'w-full'

const showsAirpodsTopBar = (isAirpodsCampaign: boolean, layout: ReturnType<typeof getStepLayout>) =>
  isAirpodsCampaign && !layout.isSuccessStep

const FlowStepMessages = ({
  alertId,
  stepValidationKey,
  purchaseError,
}: {
  alertId: string
  stepValidationKey: string | null
  purchaseError: string | null | undefined
}) => {
  const { t } = useTranslation()
  const { stepValidationAlert, purchaseErrorBanner, purchaseErrorText } = stepContent()
  return (
    <>
      {stepValidationKey ? (
        <StepValidationAlert
          className={stepValidationAlert()}
          id={alertId}
          message={t(stepValidationKey)}
        />
      ) : null}
      {purchaseError ? (
        <View className={purchaseErrorBanner()}>
          <Text className={purchaseErrorText()}>{purchaseError}</Text>
        </View>
      ) : null}
    </>
  )
}

const FlowStepModals = ({
  otp,
  airpodsNotice,
}: {
  otp: React.ComponentProps<typeof OtpModal>['otp']
  airpodsNotice: { visible: boolean; onContinue: () => void }
}) => (
  <>
    {otp.modalVisible && <OtpModal otp={otp} />}
    {airpodsNotice.visible && <AirpodsNotEligibleModal onContinue={airpodsNotice.onContinue} />}
  </>
)

const useFlowPricing = (options: Omit<Parameters<typeof useFlowDiscounts>[0], 'featureFlags'>) => {
  const featureFlags = useFlowFeatureFlags({
    config: options.config,
    appliedCoupon: options.appliedCoupon,
  })
  return { featureFlags, ...useFlowDiscounts({ ...options, featureFlags }) }
}

const hasStepFailed = (
  query: { isError: boolean },
  mutation: { isError: boolean },
  stepValidationKey: string | null
) => query.isError || (mutation.isError && !stepValidationKey)

const FlowStepPage = ({
  config,
  pageProps,
}: {
  config: FlowStepPageConfig
  pageProps: FlowStepPageProps
}) => {
  const { t } = useTranslation()
  const { track } = useAnalytics()
  const formatter = useFormatting()
  const [language, market] = getLocaleParts(pageProps.locale)
  const { urlCoupon, whitelistedParams } = useFlowQueryParams()

  const {
    form,
    isFetchingPrice,
    latestPriceData,
    localStepConditions,
    productGeneration,
    query,
    refetchPrice,
    isPriceRequestPending,
    isFormSyncedForStep,
    isAuthenticated,
    stepFlags,
    coupon,
    couponMutation,
    appliedCoupon,
    couponPending,
  } = useFlowStepSession({ config, pageProps, language, market, urlCoupon })

  const flowValues = form.watch()
  const premiumInterval = getPremiumInterval(flowValues)
  const isAirpodsCampaign = isAirpodsCoupon(appliedCoupon?.code)

  const {
    featureFlags,
    grossPremium,
    permanentDiscount,
    permanentDiscountLost,
    referralDiscount,
    selectedPlan,
  } = useFlowPricing({
    config,
    appliedCoupon,
    flowValues,
    isAuthenticated,
    latestPriceData,
    premiumInterval,
    productGeneration,
  })

  const {
    airpodsNotice,
    backBtnPressed,
    checkingEmail,
    setBackBtnPressed,
    mutation,
    navigating,
    onSubmit,
    otp,
    purchaseError,
    purchasing,
  } = useFlowSubmit({
    config,
    props: pageProps,
    language,
    market,
    appliedCoupon,
    flowValues,
    latestPriceData,
    onCouponDropped: coupon.clear,
    permanentDiscount,
    stepFlags,
    whitelistedParams,
  })

  const flowSessionId = readFlowSessionId(config.flowName)

  const markPriceChangeAction = useUserPriceChangedEvent({
    productKey: config.productKey,
    premiumInterval: getPremiumIntervalFromValues(extractTrackingFlowValues(flowValues)),
    flowSessionId,
    priceData: latestPriceData,
    track,
    eventName: EVENTS.UserPriceChanged,
  })

  const quoteDataValue: QuoteDataContextValue = {
    priceData: latestPriceData,
    isFetchingPrice,
    productGeneration,
    premiumInterval,
    permanentDiscount,
    referralDiscount,
    couponInfo: appliedCoupon,
    featureFlags,
    flowName: config.flowName,
    flowSessionId,
    flowValues,
    trackPriceChange: (action: PriceChangeUserAction) => {
      markPriceChangeAction(action)
      if (!config.livePricePaths) {
        refetchPrice()
      }
    },
    trackEvent: track,
    updateFlowValues: (values) => {
      Object.entries(values).forEach(([key, value]) => form.setValue(key, value))
    },
    formatCurrency: (amount) => formatter.formatPrice(amount),
  }

  const summary = useFlowSummary({
    config,
    appliedCoupon,
    flowValues,
    grossPremium,
    language,
    market,
    permanentDiscount,
    productGeneration,
    referralDiscount,
    selectedPlan,
  })

  const componentMap = useFlowComponentMap({
    config,
    isAirpodsCampaign,
    language,
    market,
    onSubmit,
    permanentDiscountLost,
    premiumInterval,
    priceData: latestPriceData,
    quoteDataValue,
    summary,
  })

  const { successContainer, orderSummaryCard } = stepContent()
  const isDesktop = useMediaQuery({ query: `(min-width: ${SCREEN_SIZE.XL}px)` })

  const stepValidationKey = getStepValidationKey(mutation.error)

  if (hasStepFailed(query, mutation, stepValidationKey)) {
    return (
      <SomethingWentWrongFlow error={query.error || mutation.error} flowName={config.flowName} />
    )
  }
  if (!query.isSuccess) {
    return <Spinner fullScreen />
  }

  const data = query.data
  const layout = getStepLayout(config, data, {
    navigating,
    isPlaceholderData: query.isPlaceholderData,
  })
  const textProcessor = processStepContentElementText(t, { flowValues, servicesResponses: {} })

  const OrderSummary = getOrderSummaryComponent(config)
  const renderOrderSummary = (placement: FlowOrderSummaryProps['placement']) => (
    <OrderSummary
      placement={placement}
      coupon={{ ...coupon, appliedCoupon }}
      formatCurrency={(amount) => formatter.formatPrice(amount)}
      i18nNamespace={config.i18nNamespace}
      isCouponPending={couponPending}
      isQuoteStep={layout.isQuoteStep}
      onApplyCoupon={() => couponMutation.mutate({ code: coupon.code, language, market })}
      quoteDataValue={quoteDataValue}
      summary={summary}
    />
  )

  const renderFooter = () =>
    layout.isTransitioning ? undefined : (
      <FlowStepFooter
        alignWithContent={layout.wideOrderSummary}
        newDesign={config.newDesign}
        arePaymentDetailsValid={flowValues.arePaymentDetailsValid === true}
        buyButtonLabelPrefix={config.buyButtonLabelPrefix}
        consentMissing={flowValues.immediateCoverageConsentMissing === true}
        data={data}
        flowName={config.flowName}
        form={form}
        isSubmitting={mutation.isPending || couponPending || checkingEmail}
        isPriceRequestPending={isPriceRequestPending}
        onBack={() => setBackBtnPressed(true)}
        purchasing={purchasing}
        summary={summary}
        textProcessor={textProcessor}
        whitelistedParams={whitelistedParams}
      />
    )

  const renderTopBar = () =>
    showsAirpodsTopBar(isAirpodsCampaign, layout) ? (
      <AirpodsTopBar productName={t(`${config.i18nNamespace}:headerLabel`)} />
    ) : undefined

  const renderBody = () => (layout.isTransitioning ? <Spinner fullScreen /> : renderStepContent())

  const renderStepContent = () => (
    <View
      className={successContainer({
        wideOrderSummary: layout.wideOrderSummary,
        newDesign: config.newDesign,
      })}
    >
      <View className={getContentWrapperClassName(layout)}>
        <View className={getContentClassName(config, layout)} testID={`step_${pageProps.stepName}`}>
          <FlowStepBackLink
            flowName={config.flowName}
            previousStep={data.previousStep}
            hidden={layout.isSuccessStep || !config.newDesign}
            whitelistedParams={whitelistedParams}
            onBack={() => setBackBtnPressed(true)}
          />
          <FlowStepMessages
            alertId={`step_error_${pageProps.stepName}`}
            stepValidationKey={stepValidationKey}
            purchaseError={purchaseError}
          />
          <StepContentRenderer
            servicesResponses={{}}
            flowName={config.flowName}
            locale={`${language}-${market}`}
            flowValues={flowValues}
            // @ts-expect-error ReadonlyDeep<AnyStepContentElement> vs mutable AnyStepContentElement
            elements={applyLiveConditions(data.stepContent, localStepConditions)}
            track={track}
            onSubmit={onSubmit}
            isSubmitting={mutation.isPending || couponPending || layout.isTransitioning}
            apiService={getApiService({ language })}
            apiBases={apiBases}
            componentMap={componentMap}
            progressive={isProgressiveStep(config, pageProps.stepName)}
          />
          {layout.showOrderSummary && !isDesktop && (
            <View className={orderSummaryCard({ wideOrderSummary: layout.wideOrderSummary })}>
              {renderOrderSummary('inline')}
            </View>
          )}
        </View>
        <DesktopOrderSummary
          show={layout.showOrderSummary && isDesktop}
          wide={layout.wideOrderSummary}
        >
          {renderOrderSummary('sidebar')}
        </DesktopOrderSummary>
      </View>
    </View>
  )

  return (
    <ZodForm
      form={form}
      handleSubmit={onSubmit}
      autoSubmit={canAutoSubmitStep({
        backBtnPressed,
        couponPending,
        isFormSyncedForStep,
        isPriceRequestPending,
        isTransitioning: layout.isTransitioning,
        stepContent: data.stepContent,
      })}
      stepName={pageProps.stepName}
      focusNextFieldOnEnter={config.newDesign}
    >
      <FieldSizeProvider value={config.newDesign ? 'large' : 'default'}>
        <BaseFlowLayout
          flowName={config.flowName}
          flowButtons={renderFooter()}
          progressBar={
            <FlowProgress
              items={data.progressBarData.map((section) => ({
                ...section,
                label: t(section.label),
              }))}
              showMobileStories={config.newDesign}
              centerLabels={config.newDesign}
            />
          }
          flowTitle={t(`${config.i18nNamespace}:headerLabel`)}
          alternateLinkPath={`${config.flowName}/${pageProps.stepName}`}
          firstStepName={pageProps.firstStepName}
          baseLayoutData={pageProps.baseLayoutData}
          phoneNumbers={config.phoneNumbers}
          stepName={pageProps.stepName}
          enableResetSessionModal
          hideStartOver={pageProps.enteredViaSessionLink}
          wideContent={layout.wideContent}
          alignFooterWithContent={layout.wideOrderSummary}
          newDesign={config.newDesign}
          topBar={renderTopBar()}
        >
          {renderBody()}
        </BaseFlowLayout>
      </FieldSizeProvider>
      <FlowStepModals otp={otp} airpodsNotice={airpodsNotice} />
    </ZodForm>
  )
}

export const createFlowStepPage = (config: FlowStepPageConfig) => {
  const FlowStep = (pageProps: FlowStepPageProps) => (
    <FlowStepPage config={config} pageProps={pageProps} />
  )
  FlowStep.displayName = `FlowStep(${config.flowName})`
  return FlowStep
}
